import type { ExactInput } from 'app/lib/exactInput'
import { makeObservable, observable, computed, action, runInAction, set, isObservable } from 'mobx'
import type { Client, AnyVariables, DocumentInput, OperationResult, RequestPolicy } from '@urql/core'
import type { TypedDocumentNode, ResultOf } from '@graphql-typed-document-node/core'
import { notifyError } from 'app/services/notify.client'

export type ListResult<T> = {
  hits: T[]
  total: number
  page: number
}

export type ListParams = {
  search?: string
  pagination?: { page?: number; limit?: number }
  sort?: { field: string; direction: 'ASC' | 'DESC' }
  [key: string]: unknown
}

/** What `EntityStore.search` returns: the interned hits, and the read's error if it failed. */
export type SearchResult<T> = ListResult<T> & { error?: OperationResult['error'] }

type MutationDocument = TypedDocumentNode<any, any> | undefined
type MutationInput<Document> =
  Document extends TypedDocumentNode<any, infer Variables>
    ? Variables extends { input: infer Input }
      ? Input
      : never
    : never

type EntityStoreConfig<T, TListResult, CreateDocument, UpdateDocument> = {
  listQuery?: DocumentInput<unknown, AnyVariables>
  getQuery?: DocumentInput<unknown, AnyVariables>
  createMutation?: CreateDocument
  updateMutation?: UpdateDocument
  extractList: (data: unknown) => TListResult
  extractOne?: (data: unknown) => T | undefined
  extractMutated?: (data: unknown) => T | undefined
} & (
  | {
      deleteMutation: DocumentInput<unknown, AnyVariables>
      /**
       * The entity's GraphQL `__typename`. Delete mutations return a bare Boolean, which
       * carries no typename, so urql's document cache would keep every cached list of this
       * entity and hand the deleted row back on the next cache-first fetch. Passing the
       * typename as `additionalTypenames` makes the cache drop those queries instead.
       */
      typename: string
    }
  | { deleteMutation?: undefined; typename?: undefined }
)

/** A plain object that carries a string `id` — the shape the identity map merges by id. */
function hasId(value: unknown): value is Record<string, unknown> & { id: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { id?: unknown }).id === 'string'
  )
}

/**
 * Merge the defined keys of `source` into `target` in place (ADR-0002 field-present merge).
 *
 * - A key whose `source` value is `undefined` is skipped, so a lean projection never nulls a
 *   field a richer fetch already set.
 * - A nested id-bearing object merges recursively by the same rule, so `leadOwner`/`pipelines`
 *   relations stay consistent across list and detail without being replaced wholesale.
 * - An array of id-bearing objects merges element by element, matched by `id`; this preserves
 *   each element's own identity-style merge. A scalar array (e.g. `tags`) has no ids, so it is
 *   replaced — the server list is authoritative for membership.
 * - Any other value (scalar, `null`, non-id object) is assigned directly.
 *
 * `set` is MobX's observable-safe assignment: it adds new observable keys to an already
 * observable `target` so late-arriving detail fields become reactive.
 */
function mergeDefined(target: Record<string, unknown>, source: Record<string, unknown>): void {
  for (const key of Object.keys(source)) {
    const next = source[key]
    if (next === undefined) continue
    const prev = target[key]
    if (hasId(next) && hasId(prev) && next.id === prev.id) {
      mergeDefined(prev, next)
      continue
    }
    if (Array.isArray(next) && Array.isArray(prev) && next.every(hasId) && prev.every(hasId)) {
      const prevById = new Map(prev.map((item) => [(item as { id: string }).id, item]))
      const merged = next.map((item) => {
        const existing = prevById.get((item as { id: string }).id)
        if (existing) {
          mergeDefined(existing as Record<string, unknown>, item as Record<string, unknown>)
          return existing
        }
        return item
      })
      set(target, key, merged)
      continue
    }
    set(target, key, next)
  }
}

export class EntityStore<
  T extends { id?: string },
  TListResult extends ListResult<T> = ListResult<T>,
  CreateDocument extends MutationDocument = undefined,
  UpdateDocument extends MutationDocument = undefined,
> {
  list: TListResult | undefined = undefined
  listLoading = false
  listError: string | undefined = undefined

  current: T | undefined = undefined
  currentLoading = false
  currentError: string | undefined = undefined

  saving = false
  saveError: string | undefined = undefined

  protected client: Client
  protected config: EntityStoreConfig<T, TListResult, CreateDocument, UpdateDocument>
  /** Params of the most recent `fetchList`, so `fetchNextPage` can repeat them one page on. */
  protected lastListParams: ListParams | undefined
  /**
   * Bumped by every list request. A response whose id no longer matches has been
   * superseded (the search or sort changed while it was in flight) and must not touch
   * the list, the cursor or the loading flag: without this, a page 2 response from the
   * old query would append its hits onto the new list and rewind `lastListParams`.
   */
  protected listRequestId = 0

  /**
   * Per-id identity map (ADR-0002): exactly one observable instance per entity id in this
   * store. `list.hits`, `current` and every view hold references to the instance kept here,
   * so an edit merged into it re-renders every `observer` that reads that entity. Membership
   * (which ids are in `list.hits`, and their order) stays the server-authoritative array;
   * `byId` only unifies the field *values* behind each id.
   */
  protected byId = observable.map<string, T>()

  constructor(client: Client, config: EntityStoreConfig<T, TListResult, CreateDocument, UpdateDocument>) {
    this.client = client
    this.config = config
    // makeObservable (not makeAutoObservable): EntityStore is the base class for
    // ~38 stores; MobX forbids makeAutoObservable on a subclassed class.
    makeObservable(this, {
      list: observable,
      listLoading: observable,
      listError: observable,
      current: observable,
      currentLoading: observable,
      currentError: observable,
      saving: observable,
      saveError: observable,
      byId: observable,
      hits: computed,
      total: computed,
      tableData: computed,
      listLoaded: computed,
      hasMore: computed,
      fetchList: action.bound,
      search: action.bound,
      fetchNextPage: action.bound,
      fetchOne: action.bound,
      create: action.bound,
      update: action.bound,
      remove: action.bound,
      updateInList: action.bound,
      applyPatch: action.bound,
      reset: action.bound,
    } as any)
  }

  /**
   * Return the single observable instance for `raw.id` (ADR-0002).
   *
   * - First time an id is seen: wrap `raw` as an observable, store it in `byId`, return it.
   * - Id already interned: merge only the *defined* keys of `raw` into the existing instance
   *   and return that instance. A lean list projection therefore never clobbers a richer
   *   detail instance, and `undefined` is never written over a known value (field-present
   *   merge). Nested values that carry an `id` (an object, or an array of objects) merge by
   *   the same id rule; everything else is replaced.
   * - `raw` with no `id`: cannot be interned, returned unchanged.
   */
  protected intern(raw: T | undefined): T | undefined {
    if (!raw || raw.id == null) return raw
    const existing = this.byId.get(raw.id)
    if (!existing) {
      const instance = (isObservable(raw) ? raw : observable(raw as object)) as T
      this.byId.set(raw.id, instance)
      return instance
    }
    mergeDefined(existing as Record<string, unknown>, raw as Record<string, unknown>)
    return existing
  }

  /** Map a list of raw entities through `intern`, so every hit is the shared instance for its id. */
  protected internList(raws: readonly T[]): T[] {
    return raws.map((raw) => this.intern(raw) ?? raw)
  }

  /** Intern every hit of a list result in place, returning a result whose hits are shared instances. */
  protected internListResult(result: TListResult): TListResult {
    return { ...result, hits: this.internList(result.hits) }
  }

  get hits(): T[] {
    return this.list?.hits ?? []
  }

  get total(): number {
    return this.list?.total ?? 0
  }

  get tableData(): { hits: T[]; total: number } | undefined {
    return this.list ? { hits: this.list.hits, total: this.list.total } : undefined
  }

  /** True once a first `fetchList` has stored a result (even an empty one). */
  get listLoaded(): boolean {
    return this.list !== undefined
  }

  /**
   * Whether another page remains to load. True before the first fetch (so an initial
   * load is not blocked) and whenever fewer hits are loaded than the reported total.
   * `fetchNextPage` and infinite-scroll handlers share this as the load guard.
   */
  get hasMore(): boolean {
    if (!this.list) return true
    return this.list.hits.length < this.list.total
  }

  /** The shared instance for `id` (ADR-0002), whichever read loaded it: list, detail or search. */
  lookup(id: string): T | undefined {
    return this.byId.get(id)
  }

  /** The list query's result source for `params`, with the sort direction uppercased for the SortDirection enum. */
  protected listSource(params: ListParams, requestPolicy?: RequestPolicy) {
    if (!this.config.listQuery) throw new Error('listQuery not configured')
    // Callers (URL params, table components) often pass lowercase "asc"/"desc".
    const variables = params.sort
      ? { ...params, sort: { ...params.sort, direction: params.sort.direction.toUpperCase() } }
      : params
    return this.client.query(this.config.listQuery, variables, requestPolicy ? { requestPolicy } : undefined)
  }

  /**
   * Read a list without making it the store's list: for pickers and other side searches.
   *
   * Each hit is interned (ADR-0002), so a search merges its fields into the instance that the
   * table, the drawer or `current` already hold, and a later edit of the returned hit shows in
   * every view. `list`, `listLoading` and the paging cursor are not touched, so a picker search
   * never replaces the rows of the page that the store's list feeds.
   *
   * Stale-while-revalidate: the read runs `cache-and-network`. The promise resolves with the first
   * result, which is the cached one when urql has it, so the caller shows it at once. The server
   * result then arrives in the background, is interned (so every view of those records gets the
   * latest fields) and goes to `onRevalidate`, for a caller that also wants the new membership.
   * Without a cached result, the server result resolves the promise and `onRevalidate` is not called.
   */
  search(params: ListParams = {}, opts?: { onRevalidate?: (result: SearchResult<T>) => void }) {
    const toSearchResult = (result: OperationResult): SearchResult<T> => {
      const list = result.data ? this.config.extractList(result.data) : undefined
      const hits = list ? runInAction(() => this.internList(list.hits)) : []
      return { hits, total: list?.total ?? 0, page: list?.page ?? 0, error: result.error }
    }
    return new Promise<SearchResult<T>>((resolve) => {
      let resolved = false
      let settled = false
      const subscription = this.listSource(params, 'cache-and-network').subscribe((result) => {
        const searched = toSearchResult(result)
        if (resolved) opts?.onRevalidate?.(searched)
        else resolve(searched)
        resolved = true
        // A cached result is `stale` until the network result replaces it; that one ends the read.
        if (!result.stale) {
          settled = true
          subscription?.unsubscribe()
        }
      })
      // The source can emit a settled result synchronously, before `subscription` is assigned.
      if (settled) subscription.unsubscribe()
    })
  }

  async fetchList(
    params: ListParams = {},
    opts?: { requestPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'cache-only' },
  ) {
    if (!this.config.listQuery) throw new Error('listQuery not configured')
    this.listLoading = true
    this.listError = undefined
    this.lastListParams = params
    const requestId = ++this.listRequestId
    const result = await this.listSource(params, opts?.requestPolicy).toPromise()
    runInAction(() => {
      if (requestId !== this.listRequestId) return
      this.listLoading = false
      if (result.error) {
        this.listError = result.error.message
        notifyError(result.error.message)
      }
      // Populate from any data the server returned, even alongside errors. GraphQL
      // resolvers can throw on individual non-null fields (e.g. when a column has
      // null data the schema declares as required) and urql surfaces that as
      // result.error — but result.data still carries the rest of the response.
      // Without this we'd wipe the whole list on any per-field hiccup.
      if (result.data) {
        this.list = this.internListResult(this.config.extractList(result.data))
        this.overlayList()
      }
    })
    return result
  }

  /** Runs after the current `fetchList` stores its data, for a subclass to lay over writes the read lags. */
  protected overlayList(): void {}

  /**
   * Append the page after the most recent `fetchList` onto `list.hits`.
   *
   * Infinite scroll pages through here rather than through a fetch of its own so the
   * store stays the single source of truth. The contact drawer's next/previous
   * traversal, bulk selection and the row export all read `list.hits`, and they would
   * all still see only the first page if the table appended rows into local component
   * state instead. Repeating the stored variables also keeps the search, sort and
   * limit identical to page 0, which is the only way the pages line up.
   */
  async fetchNextPage() {
    if (!this.config.listQuery) throw new Error('listQuery not configured')
    // No first page yet, a fetch already in flight, or everything is already loaded.
    if (!this.list || this.listLoading || !this.hasMore) return
    const base = this.lastListParams ?? {}
    const params: ListParams = { ...base, pagination: { ...base.pagination, page: (base.pagination?.page ?? 0) + 1 } }
    this.listLoading = true
    this.listError = undefined
    const requestId = ++this.listRequestId
    const result = await this.listSource(params).toPromise()
    runInAction(() => {
      // A newer fetchList/fetchNextPage started while this page was in flight, so these
      // hits belong to a query the user has already moved on from. Drop them.
      if (requestId !== this.listRequestId) return
      this.listLoading = false
      if (result.error) {
        this.listError = result.error.message
        notifyError(result.error.message)
        return
      }
      if (!result.data) return
      const next = this.config.extractList(result.data)
      if (!next || !this.list) return
      // Intern the new page so a contact already on an earlier page is the same instance here
      // (page-dedup), then append for membership. The spread keeps `list.hits` the ordered set.
      this.list = { ...next, hits: [...this.list.hits, ...this.internList(next.hits)] }
      // Only advance the cursor once the page is actually in, so a failed request
      // retries the same page instead of silently skipping it.
      this.lastListParams = params
    })
    return result
  }

  async fetchOne(
    id: string,
    opts?: { requestPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'cache-only' },
  ) {
    if (!this.config.getQuery) throw new Error('getQuery not configured')
    this.currentLoading = true
    this.currentError = undefined
    const result = await this.client
      .query(this.config.getQuery, { id }, opts?.requestPolicy ? { requestPolicy: opts.requestPolicy } : undefined)
      .toPromise()
    runInAction(() => {
      this.currentLoading = false
      if (result.error) this.currentError = result.error.message
      if (result.data && this.config.extractOne) this.current = this.intern(this.config.extractOne(result.data))
    })
    return result
  }

  async create<Input extends MutationInput<CreateDocument>>(
    input: Input & ExactInput<Input, MutationInput<CreateDocument>>,
  ) {
    if (!this.config.createMutation) throw new Error('createMutation not configured')
    this.saving = true
    this.saveError = undefined
    const result = await this.client
      .mutation<ResultOf<NonNullable<CreateDocument>>, AnyVariables>(this.config.createMutation, { input })
      .toPromise()
    runInAction(() => {
      this.saving = false
      if (result.error) {
        this.saveError = result.error.message
      } else if (result.data && this.config.extractMutated) {
        const created = this.intern(this.config.extractMutated(result.data))
        if (created) {
          // Prepend the interned instance for membership (new id joins the set). current and
          // list.hits[0] are now the same reference.
          if (this.list) {
            this.list = { ...this.list, hits: [created, ...this.list.hits], total: this.list.total + 1 }
          }
          this.current = created
        }
      }
    })
    return result
  }

  async update<Input extends MutationInput<UpdateDocument>>(
    id: string,
    input: Input & ExactInput<Input, MutationInput<UpdateDocument>>,
  ) {
    if (!this.config.updateMutation) throw new Error('updateMutation not configured')
    this.saving = true
    this.saveError = undefined
    const result = await this.client
      .mutation<ResultOf<NonNullable<UpdateDocument>>, AnyVariables>(this.config.updateMutation, { id, input })
      .toPromise()
    runInAction(() => {
      this.saving = false
      if (result.error) {
        this.saveError = result.error.message
      } else if (result.data && this.config.extractMutated) {
        const extracted = this.config.extractMutated(result.data)
        const updated = this.intern(extracted)
        if (updated) {
          // Interning merged the server fields into the one shared instance for this id, so the
          // matching `list.hits` element and `current` — both references to it — already reflect
          // the change. No `list.hits.map` replacement is needed (membership is unchanged); point
          // `current` at the shared instance in case it was not set before this update.
          if (this.current?.id === id) this.current = updated
        }
      }
    })
    return result
  }

  async remove(id: string) {
    if (!this.config.deleteMutation) throw new Error('deleteMutation not configured')
    this.saving = true
    this.saveError = undefined
    const result = await this.client
      .mutation(this.config.deleteMutation, { id }, { additionalTypenames: [this.config.typename] })
      .toPromise()
    runInAction(() => {
      this.saving = false
      if (result.error) {
        this.saveError = result.error.message
      } else {
        if (this.list) {
          this.list = {
            ...this.list,
            hits: this.list.hits.filter((item) => item.id !== id),
            total: this.list.total - 1,
          }
        }
        if (this.current?.id === id) this.current = undefined
        // Drop the interned instance so a later fetch of the same id starts fresh.
        this.byId.delete(id)
      }
    })
    return result
  }

  updateInList(id: string, patch: Partial<T>) {
    if (this.list) {
      this.list = { ...this.list, hits: this.list.hits.map((item) => (item.id === id ? { ...item, ...patch } : item)) }
    }
  }

  applyPatch(id: string, patch: Partial<T>) {
    // Merge a live WebSocket patch into the one shared instance (ADR-0002), rather than
    // replacing list.hits[i] and current with fresh copies. Copies detached the object an
    // open drawer holds (via focusedContact) from the identity map, so a real-time event left
    // that drawer stale. We merge the patch into the existing instance in place; every
    // observer then re-renders.
    runInAction(() => {
      // Seed byId from the fullest object we hold for this id, so a subclass that set this.list
      // with non-interned hits (e.g. ContactStore.fetchListMembers) does not lose its other
      // fields to a bare { id, ...patch }. intern() returns the shared instance.
      const current = this.current?.id === id ? this.current : undefined
      const listItem = this.list?.hits.find((item) => item.id === id)
      const base = this.byId.get(id) ?? current ?? listItem
      if (!base) return
      const shared = this.intern(base) // ensures byId holds the full object
      if (!shared) return
      mergeDefined(shared as Record<string, unknown>, patch as Record<string, unknown>)
      // Point list.hits[i] and current at the shared instance (replace any non-interned copy).
      if (this.list) {
        const i = this.list.hits.findIndex((item) => item.id === id)
        if (i !== -1 && this.list.hits[i] !== shared) {
          const hits = this.list.hits.slice()
          hits[i] = shared
          this.list = { ...this.list, hits }
        }
      }
      if (this.current?.id === id && this.current !== shared) this.current = shared
    })
  }

  reset() {
    this.list = undefined
    this.listLoading = false
    this.listError = undefined
    this.current = undefined
    this.currentLoading = false
    this.currentError = undefined
    this.saving = false
    this.saveError = undefined
    // Clear the identity map so a workspace switch (which calls reset) does not leak another
    // workspace's entity instances. A full reload already clears everything (ADR-0002).
    this.byId.clear()
  }
}
